import { pdkAxios } from '@/lib/pdk-axios';
import { protectionPlanStore } from '@/models/stores/protection-plans';
import type { Deal, ProtectionPlan } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export type UpsertProtectionPlanInput = {
  id: string;
  name: string;
  type: string;
  term_length: number;
  term_miles: number;
  monthly_price: number;
  price: number;
};

export function useProtectionPlanMutations({
  dealId,
  onSuccess,
  onError,
}: {
  dealId: Deal['id'];
  onSuccess?: (data: ProtectionPlan) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  const upsertProtectionPlanMutation = useMutation({
    onMutate: async ({
      protectionPlan,
    }: {
      protectionPlan: UpsertProtectionPlanInput;
    }) => {
      // Optimistically update the protection plan store
      protectionPlanStore.upsert({
        id: protectionPlan.id,
        deal_id: dealId,
        key: `${protectionPlan.name}_${protectionPlan.type}`
          .toLowerCase()
          .replace(/\s+/g, '_'),
        name: protectionPlan.name,
        type: protectionPlan.type,
        term_length: protectionPlan.term_length,
        term_miles: protectionPlan.term_miles,
        monthly_price: protectionPlan.monthly_price,
        price: protectionPlan.price,
      });
    },
    mutationFn: async ({
      protectionPlan,
    }: {
      protectionPlan: UpsertProtectionPlanInput;
    }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/protection-plan`, protectionPlan)
        .catch((e) => {
          console.error('Error upserting protection plan', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
      console.error('Error upserting protection plan', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
      queryClient.invalidateQueries({
        queryKey: ['protection-plan-options', dealId],
      });
    },
  });

  return { upsertProtectionPlanMutation };
}
